import React from 'react';
import { useParams } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import { paths } from 'config/path_helpers';
import { MockTest } from 'types/mock_test';

import { useQueries } from '@test_prep/app/queries';
import ContextualLink from '@test_prep/app/components/contextual_link';

import MockTestResults from './mock_test';
import OfficialQuestionSetResults from './official_question_set';
import { ExamSectionDetail } from './types';
import ViewingSharedResultsBanner from './viewing_shared_results_banner';

interface PageProps {
  examSectionDetails: ExamSectionDetail[];
  mockTest: MockTest;
  ui?: {
    initialExamSection?: string;
    saleMessage: string;
    showTutorNextStep?: boolean;
  };
  user: {
    name: string;
  };
}

const ShowMockTest: React.FC<{}> = (props) => {
  const { id, shareableId } = useParams();
  const q = useQueries();

  const pagePath = shareableId ? paths.practiceTestsShare(shareableId) : paths.practiceTest(id);
  const pageQuery = q.pageQuery(pagePath);
  const page = pageQuery.data as PageProps;

  const breadcrumbs = () => {
    if (!page) {
      return [
        { name: 'Home', path: paths.dashboard() }
      ];
    } else if (page.mockTest.template.type === 'Official Questions') {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Official Questions', path: paths.officialQuestions() },
        { name: 'Results' }
      ];
    } else {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Practice Tests', path: paths.practiceTests() },
        { name: 'Results' }
      ];
    }
  };

  return (
    <>
      {
        page && shareableId && (
          <ViewingSharedResultsBanner
            mockTestId={page.mockTest.id}
            userName={page.user.name}
          />
        )
      }

      <CardLayout breadcrumbs={breadcrumbs()} linkComponent={ContextualLink} loading={!page}>
        {({ loading }) => {
          if (loading) return;

          if (page.mockTest.template.type === 'Official Questions') {
            return (
              <OfficialQuestionSetResults
                examSectionDetails={page.examSectionDetails}
                mockTest={page.mockTest}
              />
            );
          } else {
            // cancelRefetch: a refresh requested while an earlier one is still in flight must fetch
            // again, otherwise the later grade can be missed (react-query 3 reuses the in-flight
            // promise by default)
            return (
              <MockTestResults
                refreshData={() => pageQuery.refetch({ cancelRefetch: true })}
                {...page}
              />
            );
          }
        }}
      </CardLayout>
    </>
  );
};

export default ShowMockTest;
